import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SITE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Sales Center",
  "Office Building",
  "Educational Facility",
  "Health Care / Medical",
  "Commercial Application",
  "Financing Options",
  "Other",
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", message: "" });
  const [sending, setSending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, interest: form.interest || undefined, phone: form.phone || undefined });
      toast.success("Message sent! The VBS team will get back to you shortly.");
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong sending your message. Please call us at 407-256-6141.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden bg-[#001529]">
        <img src="/images/ext-office-ground.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529] via-[#00203d]/85 to-[#00203d]/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-12 lg:pt-12 lg:pb-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-500 mb-4">Contact Us</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight max-w-3xl">
              Let's build your space
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              Tell us about your project and our team will analyze your space requirements with innovative
              solutions that meet your design and budget expectations.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pt-10 pb-16 sm:pt-12 sm:pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <Reveal className="lg:col-span-5">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 gold-underline">
              Talk to the VBS team
            </h2>
            <div className="mt-10 space-y-5">
              <a
                href={SITE.phoneHref}
                data-testid="contact-phone-card"
                className="flex items-center gap-4 bg-white rounded-xl border border-slate-100 shadow-sm p-5 hover:shadow-lg transition-shadow duration-300"
              >
                <span className="h-12 w-12 rounded-lg bg-[#00203d] flex items-center justify-center shrink-0">
                  <Phone className="h-5 w-5 text-amber-500" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.14em] font-bold text-slate-500">Call us</span>
                  <span className="block font-heading font-bold text-[#00203d] text-lg">{SITE.phone}</span>
                </span>
              </a>
              {SITE.emails.map((e) => (
                <a
                  key={e.address}
                  href={`mailto:${e.address}`}
                  data-testid={`contact-email-${e.address}`}
                  className="flex items-center gap-4 bg-white rounded-xl border border-slate-100 shadow-sm p-5 hover:shadow-lg transition-shadow duration-300"
                >
                  <span className="h-12 w-12 rounded-lg bg-[#00203d] flex items-center justify-center shrink-0">
                    <Mail className="h-5 w-5 text-amber-500" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-xs uppercase tracking-[0.14em] font-bold text-slate-500">{e.label}</span>
                    <span className="block font-semibold text-[#00203d]">{e.address}</span>
                  </span>
                </a>
              ))}
              <div data-testid="contact-area-card" className="flex items-center gap-4 bg-white rounded-xl border border-slate-100 shadow-sm p-5">
                <span className="h-12 w-12 rounded-lg bg-[#00203d] flex items-center justify-center shrink-0">
                  <MapPin className="h-5 w-5 text-amber-500" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.14em] font-bold text-slate-500">Service area</span>
                  <span className="block font-semibold text-[#00203d]">{SITE.serviceArea}</span>
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 sm:p-10 space-y-6"
            >
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900">Request a quote</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">Full name *</Label>
                  <Input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Jane Smith"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email">Email *</Label>
                  <Input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="jane@company.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-phone">Phone</Label>
                  <Input
                    id="contact-phone"
                    data-testid="contact-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="(555) 555-5555"
                  />
                </div>
                <div className="space-y-2">
                  <Label>I'm interested in</Label>
                  <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                    <SelectTrigger data-testid="contact-interest-select" aria-label="Project interest">
                      <SelectValue placeholder="Select a building type" />
                    </SelectTrigger>
                    <SelectContent>
                      {INTERESTS.map((i) => (
                        <SelectItem key={i} value={i} data-testid={`contact-interest-${i.toLowerCase().replace(/[\s/]+/g, "-")}`}>
                          {i}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-message">Tell us about your project *</Label>
                <Textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Square footage, timeline, location, and any must-haves..."
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-bold px-8 py-3.5 rounded-md transition-colors duration-200"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Send className="h-4 w-4" aria-hidden="true" />
                )}
                {sending ? "Sending..." : "Send message"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
